import { z } from 'zod';
import { ChannelId } from './common.js';
import { RarityKey } from './rarity.js';

/**
 * An ITEM is the collectible. The thing a person says they own.
 *
 * This is the layer the predecessors did not have, and its absence is why an
 * asset there accumulated `coatZones`, `patchGridSpan`, `tileMode`,
 * `wallpaperCategory`, `vinylDisplay` and `lightConfig` — everything about the
 * collectible had nowhere else to go, so it went on the file.
 *
 * Splitting them buys three things that were awkward or impossible before:
 *
 *   - one collectible with SEVERAL arts. `art.by_tier` generalises what
 *     `fullArtAssetId` was reaching for — a full-art Epic is genuinely
 *     different artwork, not a different frame.
 *   - `external_id`, so a collectible maps back to the streamer's own game.
 *   - one file backing several collectibles, which one-asset-one-item cannot
 *     express at all.
 *
 * Creation is explicit: not every file uploaded is a collectible. The bulk
 * builder is what keeps that from being tedious.
 */
export const ItemStatus = z.enum(['active', 'retired']);

export const ItemArt = z.object({
  /** The artwork unless a tier overrides it. */
  default: z.string().nullable(),
  /**
   * Art for specific rarity tiers.
   *
   * Keyed on the tier KEY, so renaming a tier does not break the mapping.
   * A tier with no entry falls back to `default`.
   */
  by_tier: z.record(RarityKey, z.string()).default({}),
});

/** What a template gave an item, so later hand-edits are visible as divergence. */
export const ItemStamp = z.object({
  name: z.string(),
  external_id: z.string(),
  kind: z.string(),
  tags: z.array(z.string()),
  at: z.string().datetime(),
});

export const Item = z.object({
  id: z.string(),
  channel_id: ChannelId,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).default(''),
  /** The streamer's own id for this thing. Unique per channel when set. */
  external_id: z.string().max(100).default(''),
  kind: z.string().default('tomo:collectible'),
  tags: z.array(z.string().min(1).max(40)).max(40).default([]),
  art: ItemArt,
  status: ItemStatus,
  /**
   * Null for a hand-made item. Present for one a template built, and the
   * values are what the template GAVE it — not what it says now.
   *
   * L67: a template is a stamp, not a link. Editing the template never
   * reaches back. Keeping the stamped values is what lets a re-apply say
   * "this will overwrite your change to the name" instead of just doing it,
   * which is the scar the Part Maker had to fix.
   */
  stamp: ItemStamp.nullable(),
  created_at: z.string().datetime(),
});

export const ItemInput = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).default(''),
  external_id: z.string().max(100).default(''),
  kind: z.string().default('tomo:collectible'),
  tags: z.array(z.string().min(1).max(40)).max(40).default([]),
  art: ItemArt.default({ default: null, by_tier: {} }),
});

export const ItemPatch = ItemInput.partial();

/**
 * The template, and the files to apply it to.
 *
 * `{name}`, `{slug}` and `{index}` substitute; anything else is literal, so
 * "Season One {name}" works with no syntax to learn.
 */
export const ItemTemplate = z.object({
  name_pattern: z.string().min(1).max(200).default('{name}'),
  external_id_pattern: z.string().max(100).default(''),
  kind: z.string().default('tomo:collectible'),
  tags: z.array(z.string().min(1).max(40)).max(40).default([]),
  inherit_asset_tags: z.boolean().default(true),
  tidy_names: z.boolean().default(true),
});

export const ItemBuildRequest = z.object({
  /** 200 at a time: a season, not a library. */
  asset_ids: z.array(z.string()).min(1).max(200),
  template: ItemTemplate,
});

export const ItemDraft = z.object({
  asset_id: z.string(),
  name: z.string(),
  external_id: z.string(),
  kind: z.string(),
  tags: z.array(z.string()),
});

/**
 * What a build WOULD do.
 *
 * All-or-nothing: a build that created nineteen items and choked on the
 * twentieth leaves somebody reconciling by hand, which is the shape of half
 * their repair scripts. So it is previewed first and every problem is
 * reported at once.
 */
export const ItemBuildPreview = z.object({
  drafts: z.array(ItemDraft),
  problems: z.array(z.object({
    asset_id: z.string(),
    field: z.string(),
    code: z.string(),
    message: z.string(),
  })),
});

export type Item = z.infer<typeof Item>;
export type ItemInput = z.infer<typeof ItemInput>;
export type ItemPatch = z.infer<typeof ItemPatch>;
export type ItemTemplate = z.infer<typeof ItemTemplate>;
export type ItemBuildRequest = z.infer<typeof ItemBuildRequest>;
export type ItemBuildPreview = z.infer<typeof ItemBuildPreview>;
